/**
 * شاشة «المساعد» — محادثة واحدة، وفعل واحد مستني تأكيد.
 *
 * 🔑 المساعد بيجاوب من داتا السيستم **بصلاحيات اللي بيسأل**: اللي مش من
 *    نطاقك ما بيشوفهوش هو كمان. وأي تنفيذ بيمرّ من كارت «هعمل كذا — تمام؟»
 *    بزرار واحد — الضغطة هي اللي بتنفّذ، مش كلام المساعد.
 *
 * ⛔ الاسم «المساعد» — ممنوع أي إشارة لذكاء اصطناعي أو نماذج في نص الواجهة.
 * ⛔ الكارت بيتعرض زي ما السيرفر بعته (`label_ar`) — الشاشة ما بتصيغش الفعل.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Shell } from "@/components/Shell";
import { ErrorState, LoadingState, OfflineState } from "@/components/states/ScreenStates";
import { fmtNum } from "@/lib/format";
import { errorMessageAr, OfflineError } from "@/api/client";
import { useRouter } from "@/lib/router";
import {
  confirmAction,
  fetchConversation,
  rejectAction,
  sendMessage,
  startNewConversation,
  type AssistantMessage,
  type AssistantState,
} from "@/api/assistant";
import "@/styles/registry.css";
import "@/styles/assistant.css";

/** أسئلة البداية — نفس الأسئلة اللي الفريق بيسألها في الجروب */
const STARTERS = ["أنا فين النهارده؟", "مين واقف عندي؟", "الأسرة دي وصلت لفين؟"];

type PageState =
  | { kind: "loading" }
  | { kind: "ready"; value: AssistantState }
  | { kind: "error"; messageAr: string }
  | { kind: "offline" };

export function Assistant() {
  const { param } = useRouter();
  const from = param("from");
  const [state, setState] = useState<PageState>({ kind: "loading" });
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [errorAr, setErrorAr] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  const load = useCallback(async (signal?: AbortSignal) => {
    try {
      const value = await fetchConversation(signal);
      setState({ kind: "ready", value });
    } catch (err) {
      if ((err as Error)?.name === "AbortError") return;
      console.error("[assistant] تحميل المحادثة فشل", err);
      setState(
        err instanceof OfflineError
          ? { kind: "offline" }
          : { kind: "error", messageAr: errorMessageAr(err) },
      );
    }
  }, []);

  useEffect(() => {
    const ac = new AbortController();
    void load(ac.signal);
    return () => ac.abort();
  }, [load]);

  const value = state.kind === "ready" ? state.value : null;

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: "end" });
  }, [value?.messages.length, value?.pendingAction?.id]);

  /** تنفيذ طلب للسيرفر مع قفل ضد الدوس المزدوج ورسالة خطأ في مكانها */
  async function run(fn: () => Promise<AssistantState>) {
    if (busy) return;
    setBusy(true);
    setErrorAr(null);
    try {
      setState({ kind: "ready", value: await fn() });
    } catch (err) {
      console.error("[assistant] الطلب فشل", err);
      setErrorAr(errorMessageAr(err));
    } finally {
      setBusy(false);
    }
  }

  async function onSend(text?: string) {
    const line = (text ?? draft).trim();
    if (!line) return;
    setDraft("");
    // الرسالة بتبان فورًا — الرد بياخد ثواني والشاشة ما تسكتش
    setState((cur) =>
      cur.kind === "ready"
        ? {
            kind: "ready",
            value: {
              ...cur.value,
              messages: [...cur.value.messages, { role: "user", text: line, at: null }],
            },
          }
        : cur,
    );
    await run(() => sendMessage(line, from ? { path: from } : {}));
    inputRef.current?.focus();
  }

  if (state.kind === "error") {
    return (
      <Shell title="المساعد">
        <ErrorState messageAr={state.messageAr} onRetry={() => void load()} />
      </Shell>
    );
  }
  if (state.kind === "offline") {
    return (
      <Shell title="المساعد">
        <OfflineState onRetry={() => void load()} />
      </Shell>
    );
  }
  if (!value) {
    return (
      <Shell title="المساعد">
        <LoadingState rows={3} />
      </Shell>
    );
  }

  if (!value.enabled) {
    return (
      <Shell title="المساعد">
        <div className="reg-card" style={{ padding: 20, lineHeight: 2 }}>
          <div className="sec-title">المساعد مش شغّال لسه</div>
          <p className="t-meta">مفتاحه لسه ما اتحطش في إعدادات السيستم — المدير بيعرف.</p>
        </div>
      </Shell>
    );
  }

  const crumb = (
    <>
      باقي <span className="num">{fmtNum(value.remainingToday)}</span> رسالة النهارده
    </>
  );

  return (
    <Shell title="المساعد" crumb={crumb}>
      <div className="reg-card asst">
        <div className="asst-head">
          <span className="t-meta">
            بيجاوب من داتا السيستم في حدود صلاحيتك، وما بينفّذش حاجة إلا لما تضغط «تمام نفّذ».
          </span>
          <button
            type="button"
            className="btn sm ghost"
            disabled={busy || value.messages.length === 0}
            onClick={() => void run(startNewConversation)}
          >
            محادثة جديدة
          </button>
        </div>

        <div className="asst-log" role="log" aria-live="polite">
          {value.messages.length === 0 ? (
            <div className="asst-empty">
              <div className="t-meta">اسأل بالعامية زي ما بتسأل زميلك. مثلًا:</div>
              <div className="asst-starters">
                {STARTERS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    className="btn sm ghost"
                    disabled={busy}
                    onClick={() => void onSend(s)}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          {value.messages.map((m, i) => (
            <Bubble
              key={i}
              m={m}
              pendingId={value.pendingAction?.id ?? null}
              busy={busy}
              onConfirm={(id) => void run(() => confirmAction(id))}
              onReject={(id) => void run(() => rejectAction(id))}
            />
          ))}

          {busy ? <div className="asst-msg is-assistant asst-typing">بيبص في السيستم…</div> : null}
          {errorAr ? (
            <div className="t-meta" role="alert" style={{ color: "var(--danger)", padding: "4px 2px" }}>
              {errorAr}
            </div>
          ) : null}
          <div ref={endRef} />
        </div>

        <form
          className="asst-compose"
          onSubmit={(e) => {
            e.preventDefault();
            void onSend();
          }}
        >
          <textarea
            ref={inputRef}
            className="reg-input asst-input"
            rows={1}
            value={draft}
            maxLength={2000}
            placeholder={value.pendingAction ? "اضغط «تمام نفّذ» أو «لا» فوق — أو اكتب وهيعتبرها «لا»" : "اكتب سؤالك…"}
            disabled={busy || value.remainingToday <= 0}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                void onSend();
              }
            }}
          />
          <button
            type="submit"
            className="btn sm primary"
            disabled={busy || draft.trim().length === 0 || value.remainingToday <= 0}
          >
            ابعت
          </button>
        </form>
        {value.remainingToday <= 0 ? (
          <div className="t-meta" style={{ padding: "0 14px 12px" }}>
            خلّصت رسائل النهارده مع المساعد — بيرجع بكرة.
          </div>
        ) : null}
      </div>
    </Shell>
  );
}

function Bubble({
  m,
  pendingId,
  busy,
  onConfirm,
  onReject,
}: {
  m: AssistantMessage;
  pendingId: string | null;
  busy: boolean;
  onConfirm: (id: string) => void;
  onReject: (id: string) => void;
}) {
  const isPending = Boolean(m.action && pendingId && m.action.id === pendingId);
  return (
    <div className={`asst-msg is-${m.role}`}>
      <div className="asst-text">{m.text}</div>
      {m.action ? (
        <div className={`asst-card${isPending ? "" : " is-done"}`}>
          <div className="asst-card-label">
            <bdi>{m.action.label_ar}</bdi>
          </div>
          {isPending ? (
            <div className="asst-card-actions">
              <button
                type="button"
                className="btn sm primary"
                disabled={busy}
                onClick={() => onConfirm(m.action!.id)}
              >
                تمام نفّذ
              </button>
              <button type="button" className="btn sm ghost" disabled={busy} onClick={() => onReject(m.action!.id)}>
                لا
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
      {m.resolved ? (
        <div className={`t-meta asst-resolved${m.resolved.ok === false ? " is-bad" : ""}`}>
          {!m.resolved.confirmed
            ? "ما اتنفّذش."
            : m.resolved.ok
              ? "اتنفّذ."
              : `السيستم رفض: ${m.resolved.error_ar ?? ""}`}
        </div>
      ) : null}
    </div>
  );
}
